import { useEffect, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface User {
  id: number;
  name: string;
  email: string;
}

interface CursorPosition {
  x: number;
  y: number;
  itemId?: number;
  columnId?: number;
}

interface ActiveUser {
  userId: number;
  userName: string;
  cursor?: CursorPosition;
}

interface UseRealtimeCollaborationProps {
  boardId: number;
  user: User;
  onItemCreated?: (item: any) => void;
  onItemUpdated?: (item: any) => void;
  onItemDeleted?: (itemId: number) => void;
  onColumnValueChanged?: (data: { itemId: number; columnId: number; value: any }) => void;
}

export function useRealtimeCollaboration({
  boardId,
  user,
  onItemCreated,
  onItemUpdated,
  onItemDeleted,
  onColumnValueChanged,
}: UseRealtimeCollaborationProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [lockedCells, setLockedCells] = useState<Map<string, { userId: number; userName: string }>>(new Map());

  useEffect(() => {
    // Connect to WebSocket server
    const socketInstance = io(window.location.origin, {
      transports: ['websocket', 'polling'],
    });

    socketInstance.on('connect', () => {
      console.log('Connected to WebSocket');
      socketInstance.emit('join-board', { boardId, user });
    });

    // Active users
    socketInstance.on('active-users', (users: ActiveUser[]) => {
      setActiveUsers(users);
    });

    socketInstance.on('user-joined', ({ userId, userName }: { userId: number; userName: string }) => {
      setActiveUsers(prev => [...prev, { userId, userName }]);
    });

    socketInstance.on('user-left', ({ userId }: { userId: number }) => {
      setActiveUsers(prev => prev.filter(u => u.userId !== userId));
    });

    // Cursor updates
    socketInstance.on('cursor-update', ({ userId, userName, cursor }: { userId: number; userName: string; cursor: CursorPosition }) => {
      setActiveUsers(prev => 
        prev.map(u => u.userId === userId ? { ...u, cursor } : u)
      );
    });

    // Cell locking
    socketInstance.on('cell-locked', ({ userId, userName, itemId, columnId }: any) => {
      setLockedCells(prev => {
        const newMap = new Map(prev);
        newMap.set(`${itemId}-${columnId}`, { userId, userName });
        return newMap;
      });
    });

    socketInstance.on('cell-unlocked', ({ itemId, columnId }: { itemId: number; columnId: number }) => {
      setLockedCells(prev => {
        const newMap = new Map(prev);
        newMap.delete(`${itemId}-${columnId}`);
        return newMap;
      });
    });

    // Real-time updates
    socketInstance.on('item-created', (item: any) => {
      onItemCreated?.(item);
    });

    socketInstance.on('item-updated', (item: any) => {
      onItemUpdated?.(item);
    });

    socketInstance.on('item-deleted', ({ itemId }: { itemId: number }) => {
      onItemDeleted?.(itemId);
    });

    socketInstance.on('column-value-changed', (data: { itemId: number; columnId: number; value: any }) => {
      onColumnValueChanged?.(data);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.emit('leave-board', { boardId });
      socketInstance.disconnect();
    };
  }, [boardId, user.id]);

  const moveCursor = useCallback((cursor: CursorPosition) => {
    socket?.emit('cursor-move', { boardId, cursor });
  }, [socket, boardId]);

  const startEditingCell = useCallback((itemId: number, columnId: number) => {
    socket?.emit('cell-edit-start', { boardId, itemId, columnId });
  }, [socket, boardId]);

  const stopEditingCell = useCallback((itemId: number, columnId: number) => {
    socket?.emit('cell-edit-end', { boardId, itemId, columnId });
  }, [socket, boardId]);

  const broadcastItemCreated = useCallback((item: any) => {
    socket?.emit('item-created', { boardId, item });
  }, [socket, boardId]);

  const broadcastItemUpdated = useCallback((item: any) => {
    socket?.emit('item-updated', { boardId, item });
  }, [socket, boardId]);

  const broadcastItemDeleted = useCallback((itemId: number) => {
    socket?.emit('item-deleted', { boardId, itemId });
  }, [socket, boardId]);

  const broadcastColumnValueChanged = useCallback((itemId: number, columnId: number, value: any) => {
    socket?.emit('column-value-changed', { boardId, itemId, columnId, value });
  }, [socket, boardId]);

  const isCellLocked = useCallback((itemId: number, columnId: number) => {
    return lockedCells.has(`${itemId}-${columnId}`);
  }, [lockedCells]);

  const getCellLocker = useCallback((itemId: number, columnId: number) => {
    return lockedCells.get(`${itemId}-${columnId}`);
  }, [lockedCells]);

  return {
    activeUsers,
    lockedCells,
    moveCursor,
    startEditingCell,
    stopEditingCell,
    broadcastItemCreated,
    broadcastItemUpdated,
    broadcastItemDeleted,
    broadcastColumnValueChanged,
    isCellLocked,
    getCellLocker,
  };
}
